/**
 * Native replacements for Lodash string utilities
 */

/**
 * Converts a string to camelCase
 * Splits on word boundaries (spaces, hyphens, underscores, and case changes) and capitalizes each word except the first
 * @example camelCase('foo bar') // 'fooBar'
 * @example camelCase('foo-bar') // 'fooBar'
 * @example camelCase('foo_bar') // 'fooBar'
 * @example camelCase('FOO BAR') // 'fooBar'
 * @example camelCase('fooBar') // 'fooBar' (preserves existing camelCase)
 * @example camelCase('HTMLParser') // 'htmlParser' (handles acronyms)
 */
export function camelCase(str: string): string {
  if (!str) return str;

  // Split on word boundaries: spaces, hyphens, underscores, and case changes
  const words = str
    .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2') // Split acronym boundaries: 'HTMLParser' → 'HTML Parser'
    .replace(/([a-z])([A-Z])/g, '$1 $2') // Split camelCase boundaries: 'fooBar' → 'foo Bar'
    .replace(/[_-]+/g, ' ') // Replace underscores and hyphens with spaces
    .split(/\s+/) // Split on whitespace
    .filter(Boolean); // Remove empty strings

  if (words.length === 0) return '';

  // First word is lowercase, rest are capitalized
  return words
    .map((word, index) => {
      const lower = word.toLowerCase();
      return index === 0 ? lower : lower.charAt(0).toUpperCase() + lower.slice(1);
    })
    .join('');
}

/**
 * Capitalizes the first character of a string
 * @example capitalize('hello') // 'Hello'
 */
export function capitalize(str: string): string {
  if (!str) return str;
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
}

/**
 * Lowercases and converts whitespace to hyphens; strips characters other than letters, digits, and hyphens.
 * Suitable for stable DOM ids and `data-testid` suffixes derived from user-visible copy.
 * @example slugify('Best Cards & Offers') // 'best-cards--offers'
 */
export function slugify(str: string): string {
  return str
    .replace(/\s+/g, '-')
    .replace(/[^a-zA-Z0-9-]/g, '')
    .toLowerCase();
}
